/**
 * @file OrderPanel
 * @description 「订单记录」列表
 * - 支持下滑到底部加载下一页
 */

import {useMemo, useRef} from 'react';
import {Table} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {EMPTY_ICON} from '../constants';
import {useOrderList} from '../hooks/useOrderList';
import styles from './index.module.less';
import StatusView from '@/components/common/StatusView';
import {useInfiniteScroll} from '@/hooks/useInfiniteScroll';
import type {PaymentOrderListItem} from '@/services/commerce/order/interface';
import {createCx} from '@/utils/cx';
import {formatUnixDate} from '@/utils/date';
import {fenToYuan} from '@/utils/money';

const cx = createCx(styles);

const buildColumns = (): ColumnsType<PaymentOrderListItem> => [
    {
        title: <div style={{textAlign: 'left', paddingLeft: 8}}>交易时间</div>,
        dataIndex: 'payTime',
        width: '22%',
        render: (payTime: number) => formatUnixDate(payTime, 'YYYY-MM-DD HH:mm'),
    },
    {
        title: <div style={{textAlign: 'left', paddingLeft: 8}}>商品名称</div>,
        dataIndex: 'productName',
        width: '22%',
    },
    {
        title: <div style={{textAlign: 'right', paddingRight: 12}}>订单编号</div>,
        dataIndex: 'orderId',
        align: 'right',
        width: '30%',
    },
    {
        title: '交易金额',
        dataIndex: 'payAmount',
        align: 'center',
        width: '13%',
        render: (payAmount: number) => `¥${fenToYuan(payAmount)}`,
    },
    {
        title: '交易状态',
        dataIndex: 'statusDesc',
        align: 'center',
        width: '13%',
    },
];

interface OrderPanelProps {
    enabled: boolean;
}

const OrderPanel: React.FC<OrderPanelProps> = ({enabled}) => {
    const {
        items,
        isLoading,
        isError,
        isFetchingNextPage,
        isFetchNextPageError,
        hasNextPage,
        fetchNextPage,
        refetch,
    } = useOrderList(enabled);

    const scrollBoxRef = useRef<HTMLDivElement>(null);

    const columns = useMemo(() => buildColumns(), []);

    useInfiniteScroll({
        containerRef: scrollBoxRef,
        hasMore: hasNextPage,
        isFetchingNextPage,
        // 上一次触底加载失败 由用户主动重试
        isRefreshing: isFetchNextPageError,
        onLoadMore: fetchNextPage,
        enabled,
    });

    const isEmpty = !isLoading && !isError && items.length === 0;

    if (isLoading) {
        return (
            <div className={cx('placeholder')}>
                <StatusView status="loading" size="medium" loadingTheme="white" />
            </div>
        );
    }

    if (isError && items.length === 0) {
        return (
            <div className={cx('placeholder')}>
                <StatusView status="error" size="small" onRetry={refetch} />
            </div>
        );
    }

    if (isEmpty) {
        return (
            <div className={cx('empty')}>
                <img className={cx('emptyIcon')} src={EMPTY_ICON} alt="" />
                <div className={cx('emptyText')}>暂无订单记录</div>
            </div>
        );
    }

    return (
        <div className={cx('container')}>
            <div className={cx('scrollBox')} ref={scrollBoxRef}>
                <Table<PaymentOrderListItem>
                    tableLayout="fixed"
                    columns={columns}
                    dataSource={items}
                    rowKey="orderId"
                    size="small"
                    pagination={false}
                />
                {items.length > 0 && (isFetchingNextPage || isFetchNextPageError) && (
                    <div className={cx('loadMore')}>
                        {isFetchNextPageError ? (
                            <StatusView.LoadMoreError onRetry={fetchNextPage} />
                        ) : (
                            <StatusView status="loading" loadingType="spinning" size="small" />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderPanel;
